import React from 'react';
import {Place, PlaceData} from "../../../types/Place";
import {Form, Formik, FormikHelpers} from "formik";
import Label from "../../../components/Form/Label";
import Field from "../../../components/Form/Field";
import Selector from "../../../components/Form/Selector";
import FormControls from "../../../components/FormControls";
import Locator from "../../../components/Form/Locator";
import {countries} from "country-data-list";
import {getTimeZones} from "@vvo/tzdb";
import {useOperator} from "../../../context/OperatorContext";
import Alert from "../../../components/Alert";
import FieldLabel from "../../../components/Form/Form/Field/FieldLabel";

// Helpers ----

const countryOptions = countries
  .all
  .filter(country => !!country.alpha3)
  .map(country => ({
    id: country.name,
    name: country.name,
  }))

const allTimeZones = getTimeZones({includeUtc: true});
const timezoneOptions = allTimeZones.map(timezone => ({
  id: timezone.name === 'Etc/UTC'? 'UTC' : timezone.name,
  name: timezone.currentTimeFormat
}))

type Props = {
  place?: Place | null
  onSubmit: (values: PlaceData, helpers: FormikHelpers<PlaceData>) => any
  onCancel?: (helpers?: FormikHelpers<PlaceData>) => any
}

function PlaceForm({place, onSubmit, onCancel}: Props) {
  const {settings} = useOperator();

  const initialValues: PlaceData = {
    name: place?.name || '',
    description: place?.description || '',
    address: place?.address || {line1: '', line2: '', line3: '', city: '', postcode: '', country: ''},
    position: place?.position || null,
    timezone: place?.timezone || 'Europe/London',
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {formik => (
        <Form className="space-y-8 divide-y divide-gray-200">
          <div className="shadow sm:rounded-md">
            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
              <div className="space-y-8 divide-y divide-gray-200">
                <div>
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      New Place
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Add an external location that can be used as an event location.
                    </p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-6">
                      <Label htmlFor="name">Name *</Label>
                      <div className="mt-1">
                        <Field
                          type="text"
                          name="name"
                          id="name"
                          autoComplete="name"
                          maxLength="250"
                          required
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-6">
                      <Label htmlFor="description">Description</Label>
                      <div className="mt-1">
                        <Field
                          type="text"
                          name="description"
                          id="description"
                          autoComplete="description"
                          maxLength="250"
                        />
                      </div>
                    </div>

                    {/* Address Line 1 */}
                    <div className="sm:col-span-6 lg:col-span-3">
                      <Label htmlFor="address.line1">Address Line 1 *</Label>
                      <div className="mt-1">
                        <Field
                          type="text"
                          name="address.line1"
                          id="address.line1"
                          autoComplete="address-line1"
                          maxLength="250"
                          required
                        />
                      </div>
                    </div>

                    {/* Address Line 2 */}
                    <div className="sm:col-span-6 lg:col-span-3">
                      <Label htmlFor="address.line2">Address Line 2</Label>
                      <div className="mt-1">
                        <Field
                          type="text"
                          name="address.line2"
                          id="address.line2"
                          autoComplete="address-line2"
                          maxLength="250"
                        />
                      </div>
                    </div>

                    {/* Address Line 3 */}
                    <div className="sm:col-span-6 lg:col-span-3">
                      <Label htmlFor="address.line3">Address Line 3</Label>
                      <div className="mt-1">
                        <Field
                          type="text"
                          name="address.line3"
                          id="address.line3"
                          autoComplete="address-line3"
                          maxLength="250"
                        />
                      </div>
                    </div>

                    {/* Address City */}
                    <div className="sm:col-span-6 lg:col-span-3">
                      <Label htmlFor="address.city">City *</Label>
                      <div className="mt-1">
                        <Field
                          type="text"
                          name="address.city"
                          id="address.city"
                          autoComplete="address-level2"
                          maxLength="250"
                          required
                        />
                      </div>
                    </div>

                    {/* Address Post Code */}
                    <div className="sm:col-span-6 lg:col-span-2">
                      <Label htmlFor="address.postcode">
                        {settings?.localisation.locale === 'en-US'? "Zip Code *" : "Post Code *"}
                      </Label>
                      <div className="mt-1">
                        <Field
                          type="text"
                          name="address.postcode"
                          id="address.postcode"
                          autoComplete="postal-code"
                          maxLength="250"
                          required
                        />
                      </div>
                    </div>

                    {/* Address Country */}
                    <div className="sm:col-span-6 lg:col-span-4">
                      <Selector
                        label="Country *"
                        items={countryOptions}
                        selectedId={initialValues.address?.country}
                        onChange={country => formik.setFieldValue('address.country', country.id)}
                        required
                      />
                    </div>

                    <div className="sm:col-span-6 mt-4">
                      <Alert message={'Click on the pin button and select the position on the map.'} />
                    </div>

                    {/* Position */}
                    <div className="sm:col-span-6 flex flex-col gap-3">
                      <FieldLabel>Location *</FieldLabel>
                      <Locator
                        height={'26rem'}
                        type='position'
                        geometry={initialValues.position ?? undefined}
                        onChange={geometry => formik.setFieldValue('position', geometry)}
                        showSearchBox
                      />
                    </div>

                    {/* Timezone */}
                    <div className="sm:col-span-6">
                      <Selector
                        label="Timezone *"
                        items={timezoneOptions}
                        selectedId={initialValues.timezone}
                        onChange={(selected) => formik.setFieldValue('timezone', selected.id)}/>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FormControls formik={formik} onCancel={onCancel}/>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default PlaceForm;